import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Loader2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function PaymentReportsContent() {
  const { activeBranchId } = useBranch();
  const { data, isLoading } = useQuery({
    queryKey: ["accounting-payment-reports", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/accounting/payment-reports${query}`, { credentials: "include" });
      return res.json();
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Received</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600">{fmt(data?.totalReceived ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Made</span>
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <div className="text-2xl font-bold text-red-500">{fmt(data?.totalMade ?? 0)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Net Position</span>
              <Wallet className="w-4 h-4 text-blue-500" />
            </div>
            <div className={`text-2xl font-bold ${(data?.netPosition ?? 0) >= 0 ? "text-blue-600" : "text-destructive"}`}>
              {fmt(data?.netPosition ?? 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>By Payment Method</CardTitle></CardHeader>
          <CardContent>
            {data?.byMethod?.length ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.byMethod} dataKey="received" nameKey="method" cx="50%" cy="50%" outerRadius={90} paddingAngle={3}>
                      {data.byMethod.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => [fmt(v), "Received"]} contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No payment data</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Monthly Trend</CardTitle></CardHeader>
          <CardContent>
            {data?.monthlyTrend?.length ? (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => [fmt(v), ""]} contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }} />
                    <Bar dataKey="received" name="Received" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="made" name="Made" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} fillOpacity={0.7} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No trend data yet</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AccountingPaymentReports() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Payment Reports — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Analyse payment flows and trends for the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Payment Reports">
        <PaymentReportsContent />
      </PlanGate>
    </div>
  );
}
